/**
 * Centralized API Client
 * 
 * This module provides a centralized way to make API calls to the backend.
 * It handles:
 *  - Authentication headers
 *  - Token refreshing
 *  - Error handling
 *  - Request retries
 *  - Preventing race conditions during token refresh
 *  - CSRF protection
 * 
 * All API calls to the backend should go through this client to ensure
 * consistent error handling and authentication.
 */

import { refreshAccessToken } from '../context/AuthContext';

// API URL from environment
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Refresh token mutex to prevent multiple simultaneous refresh attempts
interface RefreshMutex {
  isRefreshing: boolean;
  waitingPromises: Array<{
    resolve: (value: boolean) => void;
    reject: (reason?: any) => void;
  }>;
}

const refreshMutex: RefreshMutex = {
  isRefreshing: false,
  waitingPromises: [],
};

// Helper type for request options
type ApiRequestOptions = {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  requiresAuth?: boolean;
  skipRefreshOnAuthError?: boolean;
  includeCsrf?: boolean; // Flag to include CSRF token
};

/**
 * Get CSRF token from cookies
 * 
 * Django sets a csrf token in cookies which we need to extract
 * and send back in the headers for non-GET requests
 */
const getCsrfToken = (): string | null => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    cookie = cookie.trim();
    // Look for csrftoken cookie
    if (cookie.startsWith('csrftoken=')) {
      return cookie.substring('csrftoken='.length, cookie.length);
    }
  }
  return null;
};

/**
 * Process the response from an API call
 * 
 * @param response - The fetch Response object
 * @returns Parsed response data
 * @throws Error with formatted error details
 */
const processResponse = async (response: Response) => {
  // Handle successful responses
  if (response.ok) {
    // Check if response is empty
    const text = await response.text();
    if (!text) return null;
    // Try to parse as JSON, return as text if not valid JSON
    try {
      return JSON.parse(text);
    } catch (e) {
      return text;
    }
  }

  // Handle error responses
  let errorMessage = `API Error: ${response.status} ${response.statusText}`;
  let errorData: any = {};

  try {
    errorData = await response.json();
    if (errorData.detail) {
      errorMessage = errorData.detail;
    }
  } catch (e) {
    // If we can't parse as JSON, try to get text
    try {
      const errorText = await response.text();
      if (errorText) {
        errorMessage = errorText;
      }
    } catch (e) {
      // If we can't even get text, use generic error
      errorMessage = `API Error (${response.status})`;
    }
  }

  const error: any = new Error(errorMessage);
  error.status = response.status;
  error.data = errorData;
  // Mark auth errors for special handling
  if (response.status === 401 || response.status === 403) {
    error.isAuthError = true;
  }
  
  throw error;
};

/**
 * Central function to execute a token refresh with mutex lock
 * 
 * This ensures that only one token refresh happens at a time,
 * while queuing all other requests to wait for the result.
 * 
 * @returns Promise<boolean> - True if token was refreshed successfully
 */
const executeTokenRefresh = async (): Promise<boolean> => {
  // If already refreshing, wait for that to complete
  if (refreshMutex.isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshMutex.waitingPromises.push({ resolve, reject });
    });
  }

  // Set refreshing flag and clear waiting promises
  refreshMutex.isRefreshing = true;

  try {
    // Call the actual token refresh function from AuthContext
    const refreshed = await refreshAccessToken();
    
    // Resolve all waiting promises
    refreshMutex.waitingPromises.forEach(({ resolve }) => resolve(refreshed));
    
    return refreshed;
  } catch (error) {
    // Reject all waiting promises
    refreshMutex.waitingPromises.forEach(({ reject }) => reject(error));
    throw error;
  } finally {
    // Reset mutex state
    refreshMutex.isRefreshing = false;
    refreshMutex.waitingPromises = [];
  }
};

/**
 * Main API client function that all API calls should use
 * 
 * Features:
 * - Automatically adds auth headers if requiresAuth is true
 * - Handles token refresh if API returns 401/403
 * - Retries requests with new token after refresh
 * - Processes responses and errors consistently
 * 
 * @param endpoint - API endpoint path (will be prefixed with API_URL if not a full URL)
 * @param options - Request options including method, headers, body, etc.
 * @returns Promise with parsed response data
 */
export const apiClient = async <T = any>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const {
    method = 'GET',
    headers = {},
    body,
    requiresAuth = true,
    skipRefreshOnAuthError = false,
    includeCsrf = true, // Default to including CSRF token for safety
  } = options;

  // Prepare full URL
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  
  // Prepare headers with content type if sending a body
  const requestHeaders: Record<string, string> = {
    ...headers,
    'X-Requested-With': 'XMLHttpRequest', // This helps with some CSRF configs
  };
  
  if (body && typeof body !== 'string' && !(body instanceof FormData)) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  // Add CSRF token for non-GET methods
  if (includeCsrf && method !== 'GET') {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      requestHeaders['X-CSRFToken'] = csrfToken;
    }
  }

  // Add auth token if required and available
  if (requiresAuth) {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      if (user && user.token) {
        requestHeaders['Authorization'] = `Bearer ${user.token}`;
      }
    }
  }

  // Prepare request options
  const requestOptions: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: 'include',
  };

  // Add body if provided
  if (body) {
    requestOptions.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  try {
    // Execute the request
    const response = await fetch(url, requestOptions);
    
    // If auth error and refresh not skipped, try to refresh token and retry
    if (
      (response.status === 401 || response.status === 403) &&
      requiresAuth &&
      !skipRefreshOnAuthError
    ) {
      console.log(`🔑 Auth error (${response.status}) detected for ${method} ${endpoint}, attempting token refresh...`);
      const refreshed = await executeTokenRefresh();
      
      if (refreshed) {
        console.log(`🔄 Token refreshed successfully, retrying ${method} ${endpoint}...`);
        // Update auth header with new token
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          if (user && user.token) {
            requestHeaders['Authorization'] = `Bearer ${user.token}`;
            requestOptions.headers = requestHeaders;
          }
        }
        
        // Retry the request with new token
        const retryResponse = await fetch(url, requestOptions);
        console.log(`✅ Retry ${method} ${endpoint} completed with status: ${retryResponse.status}`);
        return processResponse(retryResponse);
      } else {
        console.log(`❌ Token refresh failed, attempting to get new tokens via /auth/token/...`);
        
        // Try to get new tokens via /auth/token/
        try {
          const tokenResponse = await fetch(`${API_URL}/auth/token/`, {
            method: 'GET',
            credentials: 'include'
          });
          
          if (tokenResponse.ok) {
            console.log(`✅ Successfully retrieved new tokens from /auth/token/`);
            const tokens = await tokenResponse.json();
            
            // Update user in localStorage with new tokens
            const userJson = localStorage.getItem('user');
            if (userJson) {
              const user = JSON.parse(userJson);
              const updatedUser = {
                ...user,
                token: tokens.access,
                refresh_token: tokens.refresh
              };
              localStorage.setItem('user', JSON.stringify(updatedUser));
              
              // Update auth header with new token
              requestHeaders['Authorization'] = `Bearer ${tokens.access}`;
              requestOptions.headers = requestHeaders;
              
              // Retry the request with new token
              const retryResponse = await fetch(url, requestOptions);
              console.log(`✅ Retry ${method} ${endpoint} with new tokens completed with status: ${retryResponse.status}`);
              return processResponse(retryResponse);
            }
          } else {
            console.log(`❌ Failed to get new tokens from /auth/token/, status: ${tokenResponse.status}`);
            
            // If we're in a browser environment, we could redirect to login page here
            if (typeof window !== 'undefined') {
              // Store the current URL to redirect back after auth
              localStorage.setItem('auth_redirect', window.location.href);
              
              // Dispatch a custom event that the AuthContext can listen for
              const authFailedEvent = new CustomEvent('auth:failed', { 
                detail: { endpoint, method } 
              });
              window.dispatchEvent(authFailedEvent);
              
              console.log(`🔄 Dispatched auth:failed event, application should redirect to login`);
            }
          }
        } catch (error) {
          console.error(`❌ Error getting new tokens from /auth/token/:`, error);
        }
      }
    }
    
    // Process the response
    return processResponse(response);
  } catch (error: any) {
    // If network error or other non-HTTP error
    if (!error.status) {
      error.message = `Network error: ${error.message}`;
    }
    throw error;
  }
};

/**
 * Convenience methods for common HTTP verbs
 * 
 * These provide a more concise way to make API calls:
 * - api.get('/endpoint')
 * - api.post('/endpoint', data)
 * - api.put('/endpoint', data)
 * - api.patch('/endpoint', data)
 * - api.delete('/endpoint')
 */
export const api = {
  get: <T = any>(endpoint: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiClient<T>(endpoint, { ...options, method: 'GET' }),
    
  post: <T = any>(endpoint: string, data?: any, options?: Omit<ApiRequestOptions, 'method'>) =>
    apiClient<T>(endpoint, { ...options, method: 'POST', body: data }),
    
  put: <T = any>(endpoint: string, data?: any, options?: Omit<ApiRequestOptions, 'method'>) =>
    apiClient<T>(endpoint, { ...options, method: 'PUT', body: data }),
    
  patch: <T = any>(endpoint: string, data?: any, options?: Omit<ApiRequestOptions, 'method'>) =>
    apiClient<T>(endpoint, { ...options, method: 'PATCH', body: data }),
    
  delete: <T = any>(endpoint: string, options?: Omit<ApiRequestOptions, 'method'>) =>
    apiClient<T>(endpoint, { ...options, method: 'DELETE' }),
};

export default api;

/**
 * Competition leaderboard types + helpers
 */
export interface LeaderboardEntry {
  username: string;
  points: number;
  created_at: string;
}

/**
 * Response from starting a competitive run.
 */
export interface CompetitionRunSession {
  session_token: string;
  run_duration_seconds: number;
}

/**
 * Start a competitive run.
 *
 * The server issues a single-use session token that must accompany the matching
 * score submission. Call this when a competitive run begins; keep the returned
 * `session_token` and hand it to `submitCompetitionScore` at the end of the run.
 */
export const startCompetitionRun = (): Promise<CompetitionRunSession> =>
  api.post<CompetitionRunSession>('/scores/start/');

/**
 * Submit a completed competitive run's final score.
 *
 * Scoring is computed client-side (1000 per correct Total Outs + 1000 per
 * correct Nut Outs); the server persists the points for the logged-in user after
 * validating the single-use `sessionToken` issued by `startCompetitionRun`.
 * Returns the created leaderboard entry.
 */
export const submitCompetitionScore = (
  points: number,
  sessionToken: string
): Promise<LeaderboardEntry> =>
  api.post<LeaderboardEntry>('/scores/', { points, session_token: sessionToken });

/**
 * Fetch the top 5 competitive scores for the leaderboard.
 */
export const getLeaderboard = (): Promise<LeaderboardEntry[]> =>
  api.get<LeaderboardEntry[]>('/scores/leaderboard/');